import { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable, DataTablePagination } from "@/src/components/ui/data-table";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { SummaryRow, TotalRow } from "../model";
import { summaryColumns } from "./summaryColumns";
import { totalColumns } from "./totalColumns";
import { Button } from "@/src/components/ui/button";
import { DownloadIcon } from "lucide-react";
import { arrayToCSV, downloadCSV } from "@/src/helpers/downloadCSV";
import { fromZonedTime } from "date-fns-tz";

type SummaryData = { data: SummaryRow[]; totals: TotalRow | null } & DataTablePagination;

const PAGE_SIZE = 50;

export function SummaryTable({ from, to }: { from: string; to: string }) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<SummaryData>({
    data: [],
    totals: null,
    total: 0,
    pagination: { page: 1, pageSize: PAGE_SIZE },
  });

  const query = useMemo(() => {
    if (from && to) {
      const { fromUtc, toUtc } = buildUtcWindowFromLocalDates(from, to);
      return new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        from,
        to,
        fromUtc,
        toUtc,
      }).toString();
    } else {
      return "";
    }
  }, [from, to, page, PAGE_SIZE]);

  const fetchSummary = async () => {
    try {
      return await paymentsFetch<SummaryData>(`/api/payments/summary?${query}`, "GET");
    } catch (e) {
      console.error("Error fetching payments summary", e);
    }
  };

  useEffect(() => {
    if (from && to) {
      fetchSummary().then((res) => {
        if (res) setRows(res);
        else
          setRows({
            data: [],
            total: 0,
            totals: null,
            pagination: { page: 1, pageSize: PAGE_SIZE },
          });
      });
    }
  }, [from, to]);

  const downloadFile = useCallback(() => {
    const csvData = rows.data.map((r) => [
      r.currencies,
      Number(r.inflowOriginal).toString(),
      Number(r.remainingInflow).toString(),
      Number(r.outflowOriginal).toString(),
      Number(r.remainingOutflow).toString(),
      Number(r.exchangeRate).toString(),
    ]);

    csvData.unshift([
      "Currencies",
      "All Received (Inflow)",
      "Remaining to be received",
      "All paid (Outflow)",
      "Remaining to be paid",
      "Avg. exchange rate",
    ]);

    downloadCSV(arrayToCSV(csvData), "Payments Summary");
  }, [rows.data]);

  return (
    <div className="flex flex-col gap-6 relative">
      {!!rows.data?.length && (
        <Button className="absolute right-0 -top-12" onClick={downloadFile} size="sm" variant="link">
          <DownloadIcon className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      )}
      <div className="rounded-md bg-white border">
        <DataTable columns={summaryColumns} data={rows.data} pagination={rows} onPageClick={(p) => setPage(p)} />
      </div>
      {rows.totals && (
        <div className="rounded-md bg-white border max-w-[max-content]">
          <DataTable columns={totalColumns} data={[rows.totals]} pagination={rows} onPageClick={(p) => setPage(p)} />
        </div>
      )}
    </div>
  );
}

export function buildUtcWindowFromLocalDates(fromDate: string, toDate: string) {
  if (!fromDate && !toDate)
    return {
      fromUtc: "",
      toUtc: "",
    };
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fromUtc = fromZonedTime(`${fromDate}T00:00:00`, timeZone);

  const toUtc = fromZonedTime(`${toDate}T00:00:00`, timeZone);
  toUtc.setDate(toUtc.getDate() + 1);

  return {
    fromUtc: fromDate ? fromUtc.toISOString() : "",
    toUtc: toDate ? toUtc.toISOString() : "",
  };
}
